/**
 * Goals Page Display Algebra (Story 18.8 — AC #5)
 *
 * PURE LEAF MODULE — no React, no fetch (the 32-6 widgetAccountSelection.ts
 * precedent). A buyer can verify the page's badge/filter/sort/scorecard
 * algebra without mounting a single component. Everything the Goals page
 * shows is decided here; the page just renders the results.
 *
 * 🔴 TWO goal-status value spaces (Trap #1) — this module uses the DB /
 * Epic-18 space ONLY: GOAL_STATUS_VALUES from @provenleap/db
 * ("on_track" | "off_track" | "complete" | "paused", underscored). It must
 * NEVER import GoalWidget.tsx's STATUS_COLORS, whose GoalStatus is the
 * hyphenated widget space ("on-track" | "warning" | "danger" | "achieved" |
 * "exceeded") with different colors (its on-track is BLUE). The
 * "covers exactly four GOAL_STATUS_VALUES" test is the regression lock.
 *
 * 🔴 stored status ≠ display truth (Trap #2): the stored `status` column is
 * the 18-9 cron's notification-edge state and may be stale between ticks.
 * `displayStatus` returns the FRESHLY-DERIVED status for every non-paused
 * goal; only a goal stored as "paused" is badged paused (paused wins).
 */

// apps/web CANNOT depend on @provenleap/db (rootDir constraint — see
// widgetDataForms.tsx / CanvasEditor.tsx). The goal value-spaces are
// therefore duplicated locally and MUST stay byte-identical to 18-1's
// schema/client-goals.ts (GOAL_STATUS_VALUES) and 5.30's
// report-layout-types.ts (GOAL_TYPES / GOAL_UNIT_TYPES / GOAL_PERIOD_TYPES).
// 🔴 SYNC: packages/db/src/schema/client-goals.ts +
//          packages/db/src/schema/report-layout-types.ts

// PICKER_PLATFORMS is a pure data registry (its only import is a type-only
// `import type { IntegrationProvider }`) — no React, no icons — so it is safe
// to consume from this React-free leaf for the platform-label helpers below.
import { PICKER_PLATFORMS } from "./integrations";

// The four 18.8 badge states (DB / Epic-18 space — underscored). NOT the
// hyphenated widget space in lib/widgetData.ts (Trap #1).
export const GOAL_STATUS_VALUES = [
  "on_track",
  "off_track",
  "complete",
  "paused",
] as const;
export type GoalStatus = (typeof GOAL_STATUS_VALUES)[number];

export const GOAL_TYPES = ["budget", "metric", "custom"] as const;
export type GoalType = (typeof GOAL_TYPES)[number];

export const GOAL_UNIT_TYPES = ["currency", "number", "percentage"] as const;
export type GoalUnitType = (typeof GOAL_UNIT_TYPES)[number];

export const GOAL_PERIOD_TYPES = [
  "monthly",
  "quarterly",
  "yearly",
  "custom",
] as const;
export type GoalPeriodType = (typeof GOAL_PERIOD_TYPES)[number];

// ============================================================================
// Types — the enriched goal shape returned by GET /api/goals
// ============================================================================

/** The pacing surface from services/goal-progress.ts (read-time enrichment). */
export interface GoalProgressShape {
  percentage: number;
  daysTotal: number;
  daysElapsed: number;
  daysRemaining: number;
  expectedValue: number;
  projectedValue: number;
  onPace: boolean;
  derivedStatus: GoalStatus;
  periodStart: number;
  periodEnd: number;
}

/** One row from GET /api/goals — full ClientGoal + clientName + progress. */
export interface GoalListItem {
  id: string;
  tenantId: string;
  clientId: string;
  clientName: string;
  name: string;
  goalType: GoalType;
  metricKey: string | null;
  platform: string | null;
  targetValue: number;
  currentValue: number;
  unit: GoalUnitType;
  currencyCode: string;
  periodType: GoalPeriodType;
  periodStart: number | null;
  periodEnd: number | null;
  status: GoalStatus;
  thresholdWarning: number;
  thresholdDanger: number;
  createdAt: number | null;
  updatedAt: number | null;
  progress: GoalProgressShape;
}

export interface GoalFilters {
  client?: string;
  status?: GoalStatus;
  interval?: GoalPeriodType;
  /** Platform-keyed "metric" axis (mirrors the toolbar's Metric tab). */
  metric?: string;
}

export type GoalSortKey =
  | "name"
  | "status"
  | "progress"
  | "client"
  | "platform"
  | "metric"
  | "interval";
export type SortDirection = "asc" | "desc";

export interface ScorecardCounts {
  onTrack: number;
  offTrack: number;
  complete: number;
}

// ============================================================================
// Display status (AC #5) — paused wins, else the FRESH derived status
// ============================================================================

/**
 * The status the page badges/colors a row by. A goal stored as "paused" is
 * paused regardless of the math (an operator chose to pause it). Every other
 * goal is badged by `progress.derivedStatus` — the value computed fresh at
 * read time, NEVER the stale stored column (Trap #2).
 */
export function displayStatus(goal: GoalListItem): GoalStatus {
  if (goal.status === "paused") return "paused";
  return goal.progress.derivedStatus;
}

// ============================================================================
// Badge + bar color maps (AC #5) — keyed by the DB status value-space
// ============================================================================

export interface StatusBadgeStyle {
  /** UPPERCASE pill label. */
  label: string;
  /** Pill bg+text classes. */
  badge: string;
  /** Progress-bar fill class. */
  bar: string;
}

/**
 * Keyed by GOAL_STATUS_VALUES (NOT string literals). The colors are the
 * epic's law: on_track green, off_track red, complete blue, paused gray.
 */
export const GOAL_STATUS_BADGES: Record<GoalStatus, StatusBadgeStyle> = {
  on_track: {
    label: "ON TRACK",
    badge: "bg-emerald-100 text-emerald-700",
    bar: "bg-emerald-500",
  },
  off_track: {
    label: "OFF TRACK",
    badge: "bg-red-100 text-red-700",
    bar: "bg-red-500",
  },
  complete: {
    label: "COMPLETE",
    badge: "bg-blue-100 text-blue-700",
    bar: "bg-blue-600",
  },
  paused: {
    label: "PAUSED",
    badge: "bg-muted text-muted-foreground",
    bar: "bg-muted-foreground/40",
  },
};

/**
 * Title-case a status badge label ("OFF TRACK" → "Off Track") for the goals
 * table + card Status pills (the Reports pill design). GOAL_STATUS_BADGES.label
 * stays UPPERCASE (the detail/filter views + the regression lock rely on it).
 */
export function titleCaseStatus(label: string): string {
  return label.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

// ============================================================================
// Interval labels (AC #5) — all FOUR period types (custom must not be blank).
// Plain title-case words, rendered as normal text (no pill badge).
// ============================================================================

export const INTERVAL_BADGES: Record<GoalPeriodType, string> = {
  monthly: "Month",
  quarterly: "Quarter",
  yearly: "Year",
  custom: "Custom",
};

// ============================================================================
// Pure label helpers — shared by GoalsTable (rendering) AND the sort
// comparators below, so a column sorts by the SAME human label it shows.
// ============================================================================

/**
 * Humanize a metric key for the Metric column (title-cased, underscores/dashes
 * → spaces, camelCase split). Same logic as the report editor's
 * MetricKeyCombobox.humanizeMetricKey. Pure string → string.
 */
export function humanizeMetricKey(key: string): string {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * The platform's display name from PICKER_PLATFORMS (falls back to the raw
 * slug). Pure string → string — PICKER_PLATFORMS is a plain data array.
 */
export function platformDisplayName(platform: string): string {
  return PICKER_PLATFORMS.find((p) => p.platform === platform)?.displayName ?? platform;
}

// ============================================================================
// Filter predicate (AC #5/#7) — client / status / interval, ALL client-side
// ============================================================================

/**
 * True iff the goal passes ALL active filters. An undefined filter field is
 * "no constraint". Status filters against the DISPLAY status (so filtering by
 * "paused" returns paused rows, and filtering by "on_track" returns rows
 * whose fresh derived status is on_track) — the page badges and filters by
 * the same truth. The `metric` axis matches the goal's `platform` (the
 * toolbar's platform-keyed Metric tab).
 */
export function matchesFilters(goal: GoalListItem, filters: GoalFilters): boolean {
  if (filters.client && goal.clientId !== filters.client) return false;
  if (filters.status && displayStatus(goal) !== filters.status) return false;
  if (filters.interval && goal.periodType !== filters.interval) return false;
  if (filters.metric && goal.platform !== filters.metric) return false;
  return true;
}

export function filterGoals(goals: GoalListItem[], filters: GoalFilters): GoalListItem[] {
  return goals.filter((g) => matchesFilters(g, filters));
}

// ============================================================================
// Sort comparators (AC #5) — name / status / progress / client
// ============================================================================

// Documented fixed status order: off_track < on_track < complete < paused
// (problems first, then healthy, then done, then paused last). This is the
// canonical badge order for the "status" sort — independent of the alpha
// ordering of GOAL_STATUS_VALUES.
const STATUS_SORT_RANK: Record<GoalStatus, number> = {
  off_track: 0,
  on_track: 1,
  complete: 2,
  paused: 3,
};

export function compareByName(a: GoalListItem, b: GoalListItem): number {
  return a.name.localeCompare(b.name);
}

export function compareByStatus(a: GoalListItem, b: GoalListItem): number {
  return STATUS_SORT_RANK[displayStatus(a)] - STATUS_SORT_RANK[displayStatus(b)];
}

export function compareByProgress(a: GoalListItem, b: GoalListItem): number {
  return a.progress.percentage - b.progress.percentage;
}

export function compareByClient(a: GoalListItem, b: GoalListItem): number {
  return a.clientName.localeCompare(b.clientName);
}

/**
 * Sort by the HUMAN platform label (platformDisplayName), nulls-last. A null
 * platform.slug sorts AFTER every non-null in the asc comparator; sortGoals'
 * reverse flips them to the front on desc — the same nulls-last-flips-on-desc
 * behavior every other column already has.
 */
export function compareByPlatform(a: GoalListItem, b: GoalListItem): number {
  if (a.platform === null && b.platform === null) return 0;
  if (a.platform === null) return 1; // a is null → sorts after b
  if (b.platform === null) return -1; // b is null → sorts after a
  return platformDisplayName(a.platform).localeCompare(platformDisplayName(b.platform));
}

/**
 * Sort by the humanized metric label (humanizeMetricKey), nulls-last. Same
 * null rule as compareByPlatform.
 */
export function compareByMetric(a: GoalListItem, b: GoalListItem): number {
  if (a.metricKey === null && b.metricKey === null) return 0;
  if (a.metricKey === null) return 1;
  if (b.metricKey === null) return -1;
  return humanizeMetricKey(a.metricKey).localeCompare(humanizeMetricKey(b.metricKey));
}

// Cadence rank for the Interval sort: monthly < quarterly < yearly < custom
// (shortest cadence first, custom always last). Explicit ranks — NOT a
// localeCompare on the INTERVAL_BADGES label (which would alpha-sort
// Month/Quarter/Year/Custom wrong).
const INTERVAL_SORT_RANK: Record<GoalPeriodType, number> = {
  monthly: 0,
  quarterly: 1,
  yearly: 2,
  custom: 3,
};

export function compareByInterval(a: GoalListItem, b: GoalListItem): number {
  return INTERVAL_SORT_RANK[a.periodType] - INTERVAL_SORT_RANK[b.periodType];
}

const COMPARATORS: Record<GoalSortKey, (a: GoalListItem, b: GoalListItem) => number> = {
  name: compareByName,
  status: compareByStatus,
  progress: compareByProgress,
  client: compareByClient,
  platform: compareByPlatform,
  metric: compareByMetric,
  interval: compareByInterval,
};

/**
 * Returns a NEW sorted array (never mutates input). `dir` flips the
 * comparator's sign for descending.
 */
export function sortGoals(
  goals: GoalListItem[],
  key: GoalSortKey,
  dir: SortDirection,
): GoalListItem[] {
  const cmp = COMPARATORS[key];
  const sorted = [...goals].sort(cmp);
  return dir === "desc" ? sorted.reverse() : sorted;
}

// ============================================================================
// Scorecard counting (AC #4) — paused counts toward NONE of the three
// ============================================================================

/**
 * Counts goals by DISPLAY status into the three scorecards. Paused goals are
 * excluded from all three (the epic defines exactly three cards).
 */
export function countScorecards(goals: GoalListItem[]): ScorecardCounts {
  const counts: ScorecardCounts = { onTrack: 0, offTrack: 0, complete: 0 };
  for (const goal of goals) {
    switch (displayStatus(goal)) {
      case "on_track":
        counts.onTrack += 1;
        break;
      case "off_track":
        counts.offTrack += 1;
        break;
      case "complete":
        counts.complete += 1;
        break;
      // paused → counted by none (intentional fallthrough/no-op)
    }
  }
  return counts;
}
